import { useState, useEffect } from 'react';
import MonitorIcon from '@mui/icons-material/Monitor';
// @mui
import {
  Card,
  Table,
  Stack,
  Button,
  TableBody,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Grid,
  Box,
  CardHeader,
  CardContent,
  Paper,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';
// sections
import { TableNoData, UserTableHead, AuditTableRow } from '../../../sections/@dashboard/audits';

import { Page, TableSkeleton } from '../../../components';
import { fNumber } from '../../../utils/formatNumber';
import { getAuditRecords } from '../store/auditActions';
import { getComparator } from '../../../hooks/useTable';
import { applyFilter } from '../../../sections/@dashboard/user/utils';

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
const storedUser = JSON.parse(localStorage.getItem('profile'));
const branchRoles = ['MANAGER', 'ASST_MANAGER'];

const isManager = storedUser?.role && branchRoles.includes(storedUser.role);

export default function AuditView() {
  const dispatch = useDispatch();
  const { isLoading, auditRecords, count } = useSelector((state) => state.auditReducer);

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);
  // const [openFilter, setOpenFilter] = useState(false);
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');
  const [filterName, setFilterName] = useState('');

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (event, id) => {
    const isAsc = orderBy === id && order === 'asc';
    if (id !== '') {
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(id);
    }
  };
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = auditRecords?.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };
  const handleSelectClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected?.slice(1));
    } else if (selectedIndex === selected?.length - 1) {
      newSelected = newSelected.concat(selected?.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected?.slice(0, selectedIndex), selected?.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };
  const dataFiltered = applyFilter({
    inputData: auditRecords,
    comparator: getComparator(order, orderBy),
    filterName,
  });

  const fetchData = async (dispatch, page, rowsPerPage) => {
    try {
      const payload = {
        page: page + 1,
        rowsPerPage,
      };
      await dispatch(getAuditRecords(payload));
    } catch (error) {
      // Handle errors
      console.error('Error fetching data:', error);
    }
  };

  const handleRefreshData = () => {
    fetchData(dispatch, page, rowsPerPage);
  };

  useEffect(() => {
    fetchData(dispatch, page, rowsPerPage);
  }, [dispatch, page, rowsPerPage]);

  const denseHeight = 72;
  const isNotFound = !dataFiltered?.length && !!filterName;
  // (!dataFiltered.length && !!filterRole) ||
  // (!dataFiltered.length && !!filterStatus);

  return (
    <>
      <Page title="Audit Records">
        <Container maxWidth="xl">
          <Grid container spacing={3}>
            <Grid item xs={12} md={4} lg={4}>
              <Paper elevation={3} sx={{ py: 2, textAlign: 'center' }}>
                <Box sx={{ mb: 0.5 }}>
                  <MonitorIcon />
                </Box>
                <Typography variant="h6">{fNumber(count)}</Typography>
                <Typography variant="body2">Total</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <Card>
                <Box sx={{ p: 2 }}>
                  <Stack direction="row" sx={{ float: 'right' }} spacing={1}>
                    {/* <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => setOpenFilter(true)}
                      startIcon={<FilterListOutlinedIcon />}
                    >
                      Filter
                    </Button> */}
                    {!isManager && (
                      <Button
                        size="small"
                        variant="contained"
                        component={RouterLink}
                        to={'/dashboard/audits/new'}
                        startIcon={<Iconify icon="eva:plus-fill" />}
                      >
                        New Audit
                      </Button>
                    )}
                  </Stack>
                </Box>
                <CardHeader title={'Audit Records'} subheader={'Assets to be audited'} />
                <CardContent>
                  <Scrollbar>
                    <TableContainer sx={{ minWidth: 800 }} component={Paper}>
                      {isLoading ? (
                        <Grid container sx={{ justifyContent: 'center' }}>
                          <TableSkeleton sx={{ height: denseHeight }} />
                        </Grid>
                      ) : (
                        <Table size="small">
                          <UserTableHead
                            order={order}
                            orderBy={orderBy}
                            rowCount={auditRecords?.length}
                            numSelected={selected?.length}
                            onRequestSort={handleSort}
                            onSelectAllClick={handleSelectAllClick}
                            headLabel={[
                              { id: 'name', label: 'Name', alignRight: false },
                              { id: 'progress', label: 'Progress', alignRight: false },
                              { id: 'startDate', label: 'Start Date', alignRight: false },
                              { id: 'endDate', label: 'End Date', alignRight: false },
                              { id: 'location', label: 'Location', alignRight: false },
                              { id: 'assigned_to', label: 'AssignedTo', alignRight: false },
                              { id: 'completed_on', label: 'Completed On', alignRight: false },
                              { id: 'completed_by', label: 'Completed By', alignRight: false },
                              { id: 'status', label: 'Status', alignRight: false },
                              { id: '' },
                            ]}
                          />
                          <TableBody>
                            {(dataFiltered?.length > 0 &&
                              dataFiltered?.map(
                                (row, index) =>
                                  (row && (
                                    <AuditTableRow
                                      key={row.id}
                                      row={row}
                                      selected={selected.indexOf(row.id) !== -1}
                                      handleSelectClick={(event) => handleSelectClick(event, row.id)}
                                      onRefresh={handleRefreshData}
                                    />
                                  )) || <TableSkeleton key={index} sx={{ height: denseHeight }} />
                              )) || <TableNoData isNotFound={isNotFound} />}
                          </TableBody>
                        </Table>
                      )}
                    </TableContainer>
                  </Scrollbar>

                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    component="div"
                    count={count || 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Page>
    </>
  );
}
